import { lazy, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import { RequireAuth, RequireRole } from './components/guards.js';
import { PageBoundary } from './components/PageErrorBoundary.js';
import { configPresence } from './lib/config.js';
import { SessionProvider } from './lib/session.js';
import { loadOrReload } from './lib/staleChunk.js';
import { HomePage } from './pages/HomePage.js';
import { jobHref } from './lib/jobTabs.js';
import { parseJobNumber } from './lib/workItems.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { SignInPage } from './pages/SignInPage.js';

/**
 * A page loaded on its first visit (contexts/25): each is a chunk of its own, so the first paint
 * carries the shell, sign-in and Home — not every screen the app has. Pages are named exports; this
 * picks the one the route wants. A chunk a deploy renamed reloads the tab once (`loadOrReload`).
 */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => loadOrReload(load).then((m) => ({ default: m[name] })));
}
const AdminUsersPage = page(() => import('./pages/admin/AdminUsersPage.js'), 'AdminUsersPage');
const NotificationSettingsPage = page(() => import('./pages/admin/NotificationSettingsPage.js'), 'NotificationSettingsPage');
const EmailTemplatesPage = page(() => import('./pages/admin/EmailTemplatesPage.js'), 'EmailTemplatesPage');
const ProfilePage = page(() => import('./pages/ProfilePage.js'), 'ProfilePage');
const TodoDefaultsPage = page(() => import('./pages/admin/TodoDefaultsPage.js'), 'TodoDefaultsPage');
const FileTypesPage = page(() => import('./pages/admin/FileTypesPage.js'), 'FileTypesPage');
const PurchaseOrderSettingsPage = page(() => import('./pages/admin/PurchaseOrderSettingsPage.js'), 'PurchaseOrderSettingsPage');
const VendorsPage = page(() => import('./pages/admin/VendorsPage.js'), 'VendorsPage');
const BrexSettingsPage = page(() => import('./pages/admin/BrexSettingsPage.js'), 'BrexSettingsPage');
const QuoteSettingsPage = page(() => import('./pages/admin/QuoteSettingsPage.js'), 'QuoteSettingsPage');
const WorkStagesPage = page(() => import('./pages/admin/WorkStagesPage.js'), 'WorkStagesPage');
const MeetingImportPage = page(() => import('./pages/admin/MeetingImportPage.js'), 'MeetingImportPage');
const JobberPage = page(() => import('./pages/admin/JobberPage.js'), 'JobberPage');
const WebhooksPage = page(() => import('./pages/admin/WebhooksPage.js'), 'WebhooksPage');
const SettingsPage = page(() => import('./pages/settings/SettingsPage.js'), 'SettingsPage');
const TodosPage = page(() => import('./pages/TodosPage.js'), 'TodosPage');
const JobPage = page(() => import('./pages/JobPage.js'), 'JobPage');
const JobsListPage = page(() => import('./pages/JobsListPage.js'), 'JobsListPage');
const QuotePage = page(() => import('./pages/QuotePage.js'), 'QuotePage');
const QuotesListPage = page(() => import('./pages/QuotesListPage.js'), 'QuotesListPage');
const IssuePage = page(() => import('./pages/meetings/IssuePage.js'), 'IssuePage');
const LegacyMeetingPage = page(() => import('./pages/meetings/LegacyMeetingPage.js'), 'LegacyMeetingPage');
const LiveMeetingPage = page(() => import('./pages/meetings/LiveMeetingPage.js'), 'LiveMeetingPage');
const MeetingPage = page(() => import('./pages/meetings/MeetingPage.js'), 'MeetingPage');
const MeetingsPage = page(() => import('./pages/meetings/MeetingsPage.js'), 'MeetingsPage');
const FileOpenPage = page(() => import('./pages/FileOpenPage.js'), 'FileOpenPage');
const PurchaseOrdersPage = page(() => import('./pages/PurchaseOrdersPage.js'), 'PurchaseOrdersPage');
const BillingPage = page(() => import('./pages/BillingPage.js'), 'BillingPage');
const SchedulePage = page(() => import('./pages/SchedulePage.js'), 'SchedulePage');
const VisitRequestAccessPage = page(() => import('./pages/VisitRequestAccessPage.js'), 'VisitRequestAccessPage');
const TechNotesAccessPage = page(() => import('./pages/TechNotesAccessPage.js'), 'TechNotesAccessPage');

/**
 * The route table. One place to see every page and what guards it.
 *
 *   /signin          public
 *   /                signed in
 *   /schedule        signed in
 *   /jobs            signed in — the list
 *   /purchase-orders signed in — the warehouse's POs against jobs' line items (design 19, D-104); `?record=` /
 *                    `?transaction=` the Brex card purchase the texted link is for, the dialog opened on it (design 47, D-306)
 *   /quotes, /quotes/:number/:first?/:second?  signed in — the quotes list and the quote page (design 27), the jobs' shapes
 *   /jobs/:number    redirects to /jobs/:number/general
 *   /jobs/:number/:first  signed in — a master that is one tab (line-items, purchase-orders, documents); any older
 *                    tab address redirects to the tab's master (D-083)
 *   /jobs/:number/:first/:second  signed in — the job page: a master, one of its sub tabs; a pair
 *                    the table no longer holds redirects to the tab's master
 *   /work-items, /work-items/:number  the tracker's old addresses; The Plan is a pill on the job page now
 *                    (D-098), so a job's link redirects to its Plan and the bare one to the jobs list
 *   /meetings, /meetings/:id/:tab?, /issues/:id   signed in — meetings (design 19); a meeting the person
 *                    is not in opens locked (D-102); what it holds answers 404 from the API
 *   /to-dos          signed in — To-dos on the rail under Home: every open to-do the person can see, Mine
 *                    and a meeting in the address (TodosTasks); no /my addresses, not even redirects (Brian, 2026-10-12)
 *   /to-dos/:id      signed in — the same page with that to-do picked out and its form open: a notification
 *                    email's link (design 32)
 *   /visit-request   own page, emailed-code sign-in — Schedule a visit, from Jobber's "Click To Schedule" link (design 11, D-065, D-109)
 *   /notes           own page, the same sign-in — a tech's notes on a visit, from the job's notes link in Jobber (design 36)
 *   /settings/…      signed in + admin — accounts, notifications, calendar, jobber, webhooks, to-dos, files, purchase orders, vendors, brex, quotes, work stages, import (tabs)
 *   /admin/…         the old addresses; each redirects to its tab
 *
 * Everything under the shell is behind `RequireAuth`; a role-gated page wraps
 * itself in `RequireRole` as well. The API enforces both again (`withUser`,
 * `withRole`) — these guards decide what to draw, not what is allowed.
 *
 * Every page but Home, sign-in and not-found is loaded on its first visit (`page`, above): the shell's
 * own `PageBoundary` draws its Loading… inside the frame meanwhile; the two routes outside the shell
 * have the one here. A page that throws draws the boundary's message in its place — the shell's
 * inside the frame, this one for the shell and the routes outside it — never a white screen.
 */
export default function App() {
  const presence = configPresence();
  if (!Object.values(presence).every(Boolean)) return <Unconfigured presence={presence} />;

  return (
    <BrowserRouter>
      <SessionProvider>
        <PageBoundary>
          <Routes>
            <Route path="/signin" element={<SignInPage />} />
            {/* Outside the shell and its sign-in: Jobber's staff sign in here with an emailed code (D-065). */}
            <Route path="/visit-request" element={<VisitRequestAccessPage />} />
            <Route path="/notes" element={<TechNotesAccessPage />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/jobs" element={<JobsListPage />} />
              <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
              <Route path="/billing" element={<BillingPage />} />
              {/* A file's address (D-132): the signed link fetched, then followed. */}
              <Route path="/files/:id" element={<FileOpenPage />} />
              <Route path="/jobs/:number" element={<JobPage />} />
              <Route path="/jobs/:number/:first" element={<JobPage />} />
              <Route path="/jobs/:number/:first/:second" element={<JobPage />} />
              {/* Quotes (design 27): the jobs' address shapes over Jobber's quote number. */}
              <Route path="/quotes" element={<QuotesListPage />} />
              <Route path="/quotes/:number" element={<QuotePage />} />
              <Route path="/quotes/:number/:first" element={<QuotePage />} />
              <Route path="/quotes/:number/:first/:second" element={<QuotePage />} />
              <Route path="/work-items" element={<Navigate to="/jobs" replace />} />
              <Route path="/work-items/:number" element={<OldWorkItemsAddress />} />
              <Route path="/meetings" element={<MeetingsPage />} />
              <Route path="/meetings/:id" element={<MeetingPage />} />
              <Route path="/meetings/:id/:tab" element={<MeetingPage />} />
              <Route path="/issues/:id" element={<IssuePage />} />
              <Route path="/live-meetings/:id" element={<LiveMeetingPage />} />
              <Route path="/legacy-meetings/:id" element={<LegacyMeetingPage />} />
              <Route path="/to-dos" element={<TodosPage />} />
              <Route path="/to-dos/:id" element={<TodosPage />} />
              {/* The signed-in person's own: the email signature (design 43). */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/settings"
                element={
                  <RequireRole role="admin">
                    <SettingsPage />
                  </RequireRole>
                }
              >
                <Route index element={<Navigate to="/settings/accounts" replace />} />
                <Route path="accounts" element={<AdminUsersPage />} />
                <Route path="notifications" element={<NotificationSettingsPage />} />
                <Route path="emails" element={<EmailTemplatesPage />} />
                <Route path="calendar" element={<Navigate to="/settings/accounts" replace />} />
                <Route path="jobber" element={<JobberPage />} />
                <Route path="webhooks" element={<WebhooksPage />} />
                <Route path="to-dos" element={<TodoDefaultsPage />} />
                <Route path="checklists" element={<Navigate to="/settings/to-dos" replace />} />
                <Route path="files" element={<FileTypesPage />} />
                <Route path="purchase-orders" element={<PurchaseOrderSettingsPage />} />
                <Route path="vendors" element={<VendorsPage />} />
                <Route path="brex" element={<BrexSettingsPage />} />
                <Route path="quotes" element={<QuoteSettingsPage />} />
                <Route path="work-stages" element={<WorkStagesPage />} />
                <Route path="handoff" element={<Navigate to="/settings/to-dos" replace />} />
                <Route path="import" element={<MeetingImportPage />} />
              </Route>
              <Route path="/admin/users" element={<Navigate to="/settings/accounts" replace />} />
              <Route path="/admin/schedule-people" element={<Navigate to="/settings/accounts" replace />} />
              <Route path="/admin/jobber" element={<Navigate to="/settings/jobber" replace />} />
              <Route path="/admin/webhooks" element={<Navigate to="/settings/webhooks" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </PageBoundary>
      </SessionProvider>
    </BrowserRouter>
  );
}

/** `/work-items/18447`, the tracker's address before it became The Plan (D-098): on to the job's Plan, so a saved link keeps working. */
function OldWorkItemsAddress() {
  const { number } = useParams();
  const jobNumber = number === undefined ? null : parseJobNumber(number);
  return <Navigate to={jobNumber === null ? '/jobs' : jobHref(jobNumber, 'project-management', 'plan')} replace />;
}

/** A build with no Supabase settings cannot even sign in. Say so, once, plainly. */
function Unconfigured({ presence }: { presence: Record<string, boolean> }) {
  return (
    <main className="signin">
      <div className="signin__card">
        <h1 className="brand__name">UnitedOS</h1>
        <div className="callout callout--bad">
          <strong>This build is missing configuration.</strong>
          <ul>
            {Object.entries(presence).map(([name, ok]) => (
              <li key={name}>
                <code>{name}</code> — {ok ? 'set' : 'missing'}
              </li>
            ))}
          </ul>
          <p className="text-muted">
            Locally: copy <code>apps/web/.env.example</code> to <code>apps/web/.env</code>. It works unedited.
          </p>
        </div>
      </div>
    </main>
  );
}
